/**
 * Figures, formatted.
 *
 * Small enough to look like it belongs in a shared library, and deliberately
 * kept next to the components that use it, because its only job is to keep the
 * number a chart draws and the number its hidden table announces in agreement.
 * A screen reader told "1,204" while the axis is labelled "1.2k" has been given
 * a different reading of the same data, and the text alternative stops being an
 * alternative at that point.
 *
 * Hence two functions rather than one. `formatCount` is exact and is what every
 * text alternative uses. `formatAxis` is allowed to abbreviate, because an axis
 * has about four characters of room, and it is never used for the hidden table.
 *
 * Locale is a parameter with no default, so the browser's own resolution
 * applies. Hard-coding `en-NG` was rejected: an organization running a project
 * in Kano may well have staff reading the console in French, and a thousands
 * separator is exactly the sort of thing that quietly reads as a decimal point
 * to the wrong reader.
 */

export function formatCount(value: number, locale?: string): string {
  return new Intl.NumberFormat(locale).format(value)
}

/**
 * Abbreviated for an axis tick or a chart label, where four characters is the
 * whole budget. Never use this where the figure is the answer to a question.
 */
export function formatAxis(value: number, locale?: string): string {
  const magnitude = Math.abs(value)
  if (magnitude < 10_000) return new Intl.NumberFormat(locale).format(value)
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  )
}

/**
 * A share of a whole.
 *
 * One decimal place below ten per cent and none above it. Rounding 0.4% to 0%
 * is not a rounding, it is a claim that nobody responded, and on a broadcast
 * report that claim would be wrong.
 *
 * A zero whole returns null rather than NaN or a cheerful 0%. Nothing was sent,
 * so no share exists, and the caller has to decide what to show instead. That
 * decision belongs at the call site rather than hidden in a formatter.
 */
export function formatShare(part: number, whole: number, locale?: string): string | null {
  if (whole <= 0) return null
  const ratio = part / whole
  const digits = ratio < 0.1 ? 1 : 0
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: digits,
  }).format(ratio)
}

/**
 * A change, with its sign kept. `signDisplay: 'exceptZero'` rather than a
 * hand-built `+` prefix, because the minus sign a locale wants is not always
 * the hyphen on the keyboard.
 */
export function formatDelta(value: number, locale?: string): string {
  return new Intl.NumberFormat(locale, { signDisplay: 'exceptZero' }).format(value)
}

/**
 * A change expressed as a proportion of where it started.
 *
 * Growth from zero has no percentage, and reporting one as infinite or as 100%
 * would be inventing a number. The caller gets null and shows the absolute
 * change instead.
 */
export function formatDeltaShare(
  change: number,
  previous: number,
  locale?: string,
): string | null {
  if (previous === 0) return null
  const ratio = change / Math.abs(previous)
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    signDisplay: 'exceptZero',
    maximumFractionDigits: Math.abs(ratio) < 0.1 ? 1 : 0,
  }).format(ratio)
}
